/**
 * Pure utility functions for conversation flag and SLA computation.
 * Operates on Supabase ModerationMessage / ModerationContact shapes.
 * No React, no API calls — safe to import anywhere.
 *
 * Supports both the legacy eval_items-based flags (highrisk/lowconf/askhuman)
 * and the new per-agent custom_message_flags (FlagDetectedItem[]).
 */

import {
  ModerationMessage,
  ModerationContact,
  ContactFlagInfo,
  AggregatedEvalItem,
  DetectedItem,
  EvalItemMetaMap,
  FlagType,
} from "../types"
import {
  getMessageFlag,
  getCustomMessageFlagsOnly,
  hasBuiltinFlagColumnSignal,
  incomingHasActiveBuiltinColumn,
  BUILTIN_FLAG_STATE,
} from "./builtinFlagState"

export { getMessageFlag, getCustomMessageFlagsOnly } from "./builtinFlagState"

// SLA hours by severity level
const SLA_HOURS: Record<string, number> = {
  "5": 1,   "critical": 1,
  "4": 4,   "high": 4,
  "3": 72,  "moderate": 72,
  "2": 168, "mild": 168,
  "1": 168, "low": 168,
}

export function getSlaHours(level: string): number | null {
  const normalized = level.toLowerCase().trim()
  if (SLA_HOURS[normalized]) return SLA_HOURS[normalized]
  const numMatch = normalized.match(/\d+/)
  if (numMatch && SLA_HOURS[numMatch[0]]) return SLA_HOURS[numMatch[0]]
  for (const [key, hours] of Object.entries(SLA_HOURS)) {
    if (normalized.includes(key)) return hours
  }
  return null
}

export { type ContactFlagInfo }

export function emptyFlagInfo(): ContactFlagInfo {
  return {
    hasHighRisk: false,
    hasLowConfidence: false,
    hasAskedHuman: false,
    hasBadRating: false,
    needsReply: false,
    isResolved: false,
    hasAnyResolvedFlag: false,
    activeFlags: {},
    resolvedFlags: {},
  }
}

// ── Conversation category (Open / Pending / Resolved / New) ────────────────
//
// Single source of truth for the four moderator-facing categories, so the
// section groupings in ContactList and the filter dropdown in useConversations
// can never disagree about which bucket a conversation falls into.
//
// Priority order matters and is deliberate:
//   1. Open     — needs a moderator's attention right now
//   2. Resolved — was escalated, all flags now cleared
//   3. Pending  — manually parked by a moderator, and not already Open
//   4. New      — never escalated, nothing to do
//
// A conversation that is both "pending" (manually flagged) and has a fresh
// human-handled message awaiting reply is Open, not Pending — a moderator's
// own to-do overrides a stale "no action needed" flag from earlier.

export type ConversationCategory = "open" | "pending" | "resolved" | "new"

export function isEscalatedConversation(flags: ContactFlagInfo | undefined): boolean {
  return Boolean(
    flags?.hasHighRisk ||
    flags?.hasLowConfidence ||
    flags?.hasAskedHuman ||
    Object.keys(flags?.activeFlags || {}).length > 0
  )
}

/** Reads the moderator-set "pending" flag out of the contact's moderation_data JSON blob. */
export function parsePendingFlag(moderationData: string | null | undefined): boolean {
  if (typeof moderationData !== "string" || !moderationData.trim()) return false
  try {
    const parsed = JSON.parse(moderationData) as { pending?: unknown }
    return parsed.pending === true
  } catch {
    return false
  }
}

export function categorizeConversation(args: {
  flags: ContactFlagInfo | undefined
  handledBy: "human" | "bot"
  pending: boolean
}): ConversationCategory {
  const { flags, handledBy, pending } = args
  const escalated = isEscalatedConversation(flags)
  const resolved = Boolean(flags?.isResolved)
  const awaitingHumanReply = handledBy === "human" && Boolean(flags?.needsReply)

  if ((escalated && !resolved) || awaitingHumanReply) return "open"
  if (resolved) return "resolved"
  if (pending) return "pending"
  return "new"
}

// ── New custom_message_flags helpers (getMessageFlag from builtinFlagState) ─

export function isMessageFlagActive(flags: FlagDetectedItem[] | null | undefined, flagId: string): boolean {
  const f = getMessageFlag(flags, flagId)
  if (!f) return false
  return f.status !== "resolved"
}

export function isMessageFlagResolved(flags: FlagDetectedItem[] | null | undefined, flagId: string): boolean {
  const f = getMessageFlag(flags, flagId)
  return f?.status === "resolved"
}

export function messageHasAnyActiveFlag(flags: FlagDetectedItem[] | null | undefined): boolean {
  return (flags || []).some(f => f.status !== "resolved")
}

/** Built-in columns and/or custom-only JSON — any active flag. */
export function messageHasAnyActiveFlagCombined(m: ModerationMessage): boolean {
  if (hasBuiltinFlagColumnSignal(m)) {
    return incomingHasActiveBuiltinColumn(m) || messageHasAnyActiveFlag(getCustomMessageFlagsOnly(m.custom_message_flags))
  }
  return messageHasAnyActiveFlag(m.custom_message_flags)
}

// ── Legacy eval_items helpers (kept for backward compat) ───────────

export function getFlagItem(items: DetectedItem[] | null | undefined, itemId: number): DetectedItem | undefined {
  return (items || []).find(i => i.itemId === itemId)
}

export function isFlagged(items: DetectedItem[] | null | undefined, itemId: number): boolean {
  const item = getFlagItem(items, itemId)
  if (!item) return false
  return item.status !== "resolved"
}

export function isResolved(items: DetectedItem[] | null | undefined, itemId: number): boolean {
  const item = getFlagItem(items, itemId)
  return item?.status === "resolved"
}

export function isIncomingMessage(msg: ModerationMessage): boolean {
  return msg.role === "user" || msg.role === "synthetic"
}

export function isHITLMessage(msg: ModerationMessage): boolean {
  return msg.role === "assistant" && msg.sender != null
}

export function isBotMessage(msg: ModerationMessage): boolean {
  return msg.role === "assistant" && msg.sender == null
}

export function toUnixSeconds(created_at: string): number {
  return Math.floor(new Date(created_at).getTime() / 1000)
}

/**
 * SLA clock start:
 * - Prefer the first incoming message after the latest human (HITL) response.
 * - If there is no human response yet, use the first incoming message.
 * - Fallback to the first message timestamp.
 */
export function getSlaStartTimestamp(messages: ModerationMessage[]): number {
  if (messages.length === 0) return Math.floor(Date.now() / 1000)

  const ordered = [...messages].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  )

  let lastHumanTs = 0
  for (const m of ordered) {
    if (isHITLMessage(m)) lastHumanTs = toUnixSeconds(m.created_at)
  }

  for (const m of ordered) {
    if (isIncomingMessage(m) && toUnixSeconds(m.created_at) > lastHumanTs) {
      return toUnixSeconds(m.created_at)
    }
  }

  for (const m of ordered) {
    if (isIncomingMessage(m)) return toUnixSeconds(m.created_at)
  }

  return toUnixSeconds(ordered[0].created_at)
}

/** True if `eval_items` marks this id as any moderation-class flag. */
export function isModerationFlagItemId(itemId: number, meta: EvalItemMetaMap = {}): boolean {
  const m = meta[itemId]
  return !!(m?.highrisk || m?.lowconf || m?.askhuman)
}

function contributesToCategory(itemId: number, meta: EvalItemMetaMap, kind: FlagType): boolean {
  const m = meta[itemId]
  if (!m) return false
  if (kind === "high_risk") return m.highrisk === true
  if (kind === "low_confidence") return m.lowconf === true
  if (kind === "asked_human") return m.askhuman === true
  return false
}

/** Lowest id among eval items configured for this flag (stable default for new detections). */
export function primaryItemIdForCategory(meta: EvalItemMetaMap, kind: FlagType): number | null {
  const ids = Object.keys(meta)
    .map(Number)
    .filter(id => contributesToCategory(id, meta, kind))
    .sort((a, b) => a - b)
  return ids.length > 0 ? ids[0] : null
}

/** Existing detection for this category, else primary eval item id for new toggle — or null if nothing configured. */
export function pickFlagItemIdForToggle(flagType: FlagType, items: DetectedItem[], meta: EvalItemMetaMap = {}): number | null {
  const found = items.find(i => contributesToCategory(i.itemId as number, meta, flagType))
  if (found) return found.itemId as number
  return primaryItemIdForCategory(meta, flagType)
}

export function moderationCategoryActive(items: DetectedItem[], meta: EvalItemMetaMap, kind: FlagType): boolean {
  for (const item of items) {
    if (item.status === "resolved") continue
    if (contributesToCategory(item.itemId as number, meta, kind)) return true
  }
  return false
}

export function moderationCategoryResolved(items: DetectedItem[], meta: EvalItemMetaMap, kind: FlagType): boolean {
  for (const item of items) {
    if (item.status !== "resolved") continue
    if (contributesToCategory(item.itemId as number, meta, kind)) return true
  }
  return false
}

/**
 * True if this message carries an active moderation-class flag.
 * custom_message_flags only apply to user/synthetic messages.
 * Legacy eval_items flags can come from either user or bot messages.
 */
export function messageHasActiveModerationFlags(message: ModerationMessage, meta: EvalItemMetaMap = {}): boolean {
  if (isIncomingMessage(message)) {
    if (hasBuiltinFlagColumnSignal(message)) {
      return incomingHasActiveBuiltinColumn(message) || messageHasAnyActiveFlag(getCustomMessageFlagsOnly(message.custom_message_flags))
    }
    if (message.custom_message_flags && message.custom_message_flags.length > 0) {
      return messageHasAnyActiveFlag(message.custom_message_flags)
    }
    const userItems = message.user_detected_items || []
    return (
      moderationCategoryActive(userItems, meta, "high_risk") ||
      moderationCategoryActive(userItems, meta, "low_confidence") ||
      moderationCategoryActive(userItems, meta, "asked_human")
    )
  }

  return false
}

function applyDetectedItemsToFlagInfo(
  flagInfo: ContactFlagInfo,
  items: DetectedItem[],
  evalItemMap: EvalItemMetaMap
): void {
  // Eval items only contribute to the evalItems category badges — never to the
  // flag booleans (hasHighRisk / hasLowConfidence / hasAskedHuman). Those are
  // exclusively driven by custom_message_flags now. The highrisk/lowconf/askhuman
  // columns on eval_items are legacy and caused false positives on old messages.

  if (!flagInfo.evalItems) flagInfo.evalItems = []
  const aggregated = flagInfo.evalItems as AggregatedEvalItem[]

  for (const item of items) {
    if (isModerationFlagItemId(item.itemId as number, evalItemMap)) continue
    const name = evalItemMap[item.itemId as number]?.name
    if (!name) continue
    const existing = aggregated.find(a => a.itemId === item.itemId)
    if (existing) {
      existing.count++
      if ((item.severity ?? 0) > existing.maxSeverity) existing.maxSeverity = item.severity ?? 0
      if ((item.confidence ?? 0) > existing.maxConfidence) existing.maxConfidence = item.confidence ?? 0
      if (item.reasoning) existing.latestReasoning = item.reasoning
    } else {
      aggregated.push({
        itemId: item.itemId as number,
        name,
        maxSeverity: item.severity ?? 0,
        maxConfidence: item.confidence ?? 0,
        latestReasoning: item.reasoning,
        count: 1,
      })
    }
  }
}

/** Built-in smallint columns → ContactFlagInfo */
function applyBuiltinNumericColumnsToFlagInfo(flagInfo: ContactFlagInfo, m: ModerationMessage): void {
  if (!flagInfo.activeFlags) flagInfo.activeFlags = {}
  if (!flagInfo.resolvedFlags) flagInfo.resolvedFlags = {}

  const apply = (val: number | null | undefined, id: "high_risk" | "low_confidence" | "asked_human", setHas: () => void) => {
    if (val == null) return
    if (val === BUILTIN_FLAG_STATE.FLAGGED) {
      flagInfo.activeFlags![id] = true
      setHas()
    }
    if (val === BUILTIN_FLAG_STATE.RESOLVED) {
      flagInfo.resolvedFlags![id] = true
      flagInfo.hasAnyResolvedFlag = true
    }
  }

  apply(m.highrisk, "high_risk", () => { flagInfo.hasHighRisk = true })
  apply(m.lowconf, "low_confidence", () => { flagInfo.hasLowConfidence = true })
  apply(m.askhuman, "asked_human", () => { flagInfo.hasAskedHuman = true })
}

/** Custom-only JSON flags → dynamic maps */
function applyCustomMessageFlagsToFlagInfo(flagInfo: ContactFlagInfo, flags: FlagDetectedItem[]): void {
  if (!flagInfo.activeFlags) flagInfo.activeFlags = {}
  if (!flagInfo.resolvedFlags) flagInfo.resolvedFlags = {}

  for (const f of flags) {
    if (f.status === "resolved") {
      flagInfo.resolvedFlags[f.flagId] = true
      flagInfo.hasAnyResolvedFlag = true
    } else {
      flagInfo.activeFlags[f.flagId] = true
    }
  }
}

/** Legacy JSON that may still include built-in ids */
function applyMessageFlagsToFlagInfoLegacy(
  flagInfo: ContactFlagInfo,
  flags: FlagDetectedItem[]
): void {
  if (!flagInfo.activeFlags) flagInfo.activeFlags = {}
  if (!flagInfo.resolvedFlags) flagInfo.resolvedFlags = {}

  for (const f of flags) {
    if (f.status === "resolved") {
      flagInfo.resolvedFlags[f.flagId] = true
      flagInfo.hasAnyResolvedFlag = true
    } else {
      flagInfo.activeFlags[f.flagId] = true
      if (f.flagId === "high_risk") flagInfo.hasHighRisk = true
      else if (f.flagId === "low_confidence") flagInfo.hasLowConfidence = true
      else if (f.flagId === "asked_human") flagInfo.hasAskedHuman = true
    }
  }
}

/** Accumulate flag data and eval items from a single message into an existing FlagInfo object. */
export function accumulateFlagInfo(
  flagInfo: ContactFlagInfo,
  message: ModerationMessage,
  evalItemMap: EvalItemMetaMap = {}
): void {
  if (isIncomingMessage(message)) {
    if (hasBuiltinFlagColumnSignal(message)) {
      applyBuiltinNumericColumnsToFlagInfo(flagInfo, message)
      applyCustomMessageFlagsToFlagInfo(flagInfo, getCustomMessageFlagsOnly(message.custom_message_flags))
      applyDetectedItemsToFlagInfo(flagInfo, message.user_detected_items || [], evalItemMap)
    } else if (message.custom_message_flags != null) {
      if (message.custom_message_flags.length > 0) {
        applyMessageFlagsToFlagInfoLegacy(flagInfo, message.custom_message_flags)
      }
      applyDetectedItemsToFlagInfo(flagInfo, message.user_detected_items || [], evalItemMap)
    } else {
      applyDetectedItemsToFlagInfo(flagInfo, message.user_detected_items || [], evalItemMap)
    }
    return
  }

  if (isBotMessage(message)) {
    if (message.rating === "0") flagInfo.hasBadRating = true
  }
}

/** Compute full flag info for a contact from its complete message list. */
export function computeFlagInfoFromMessages(
  messages: ModerationMessage[],
  contact: ModerationContact,
  evalItemMap: EvalItemMetaMap = {}
): ContactFlagInfo {
  const flagInfo = emptyFlagInfo()

  for (const message of messages) {
    accumulateFlagInfo(flagInfo, message, evalItemMap)
  }

  // Sort aggregated eval items by max severity descending
  if (flagInfo.evalItems) {
    flagInfo.evalItems.sort((a, b) => b.maxSeverity - a.maxSeverity)
  }

  // SLA is based on contact-level severity, only when there are unresolved flags
  flagInfo.contactSeverity = contact.severity ?? null
  const hasUnresolved = flagInfo.hasHighRisk || flagInfo.hasLowConfidence || flagInfo.hasAskedHuman ||
    Object.keys(flagInfo.activeFlags || {}).length > 0
  if (hasUnresolved && contact.severity != null) {
    const slaHours = getSlaHours(String(contact.severity))
    if (slaHours !== null) {
      const slaStartTs = getSlaStartTimestamp(messages)
      flagInfo.flaggedAt   = slaStartTs
      flagInfo.slaDeadline = slaStartTs + slaHours * 3600
    }
    const topItem = (flagInfo.evalItems || [])[0]
    flagInfo.riskCategoryLevel = String(contact.severity)
    flagInfo.riskCategoryName  = topItem?.name || `Severity ${contact.severity}`
  }

  if (messages.length > 0) {
    const lastMsg = messages[messages.length - 1]
    const noReplyNeeded = contact.no_reply_needed === true
    flagInfo.needsReply = isIncomingMessage(lastMsg) && !noReplyNeeded
  }

  flagInfo.isResolved =
    flagInfo.hasAnyResolvedFlag &&
    !flagInfo.hasHighRisk &&
    !flagInfo.hasLowConfidence &&
    !flagInfo.hasAskedHuman &&
    Object.keys(flagInfo.activeFlags || {}).length === 0

  return flagInfo
}

/** Whether any recent message in this conversation was sent by a human operator. */
export function hasOperatorSender(messages: ModerationMessage[]): boolean {
  return messages.some(m => isHITLMessage(m))
}

/**
 * Split bot messages on the `<break>` delimiter into one message per part, so
 * each part renders as its own bubble (mirrors how workers/message.ts and the
 * telerivet integration actually send them as separate messages). Only bot
 * messages are split. The LAST part keeps the original DB row id and all its
 * moderation metadata (flags / rating / detected items) so flagging and rating
 * stay wired to the real row; earlier parts get a synthetic id and no metadata.
 */
export function expandBreaks(messages: ModerationMessage[]): ModerationMessage[] {
  const out: ModerationMessage[] = []
  for (const msg of messages) {
    const text = msg.message || ""
    if (!isBotMessage(msg) || !text.includes("<break>")) {
      out.push(msg)
      continue
    }
    const parts = text
      .split("<break>")
      .map((p) => p.trim())
      .filter(Boolean)
    if (parts.length <= 1) {
      out.push({ ...msg, message: parts[0] ?? text })
      continue
    }
    parts.forEach((part, i) => {
      const isLast = i === parts.length - 1
      if (isLast) {
        out.push({ ...msg, message: part })
      } else {
        out.push({
          ...msg,
          id: `${msg.id}::break-${i}`,
          message: part,
          highrisk: null,
          lowconf: null,
          askhuman: null,
          custom_message_flags: null,
          user_detected_items: null,
          agent_detected_items: null,
          rating: null,
          rating_example: null,
        } as ModerationMessage)
      }
    })
  }
  return out
}

/** True if two FlagInfo objects are identical (used to skip unnecessary re-renders). */
export function flagInfoEqual(a: ContactFlagInfo, b: ContactFlagInfo): boolean {
  const aItems = a.evalItems || []
  const bItems = b.evalItems || []
  if (aItems.length !== bItems.length) return false
  for (let i = 0; i < aItems.length; i++) {
    if (aItems[i].itemId !== bItems[i].itemId || aItems[i].maxSeverity !== bItems[i].maxSeverity) return false
  }

  const aActive = Object.keys(a.activeFlags || {}).sort().join(",")
  const bActive = Object.keys(b.activeFlags || {}).sort().join(",")
  const aResolved = Object.keys(a.resolvedFlags || {}).sort().join(",")
  const bResolved = Object.keys(b.resolvedFlags || {}).sort().join(",")

  return (
    a.hasHighRisk         === b.hasHighRisk &&
    a.hasLowConfidence    === b.hasLowConfidence &&
    a.hasAskedHuman       === b.hasAskedHuman &&
    a.hasBadRating        === b.hasBadRating &&
    a.needsReply          === b.needsReply &&
    a.isResolved          === b.isResolved &&
    a.riskCategoryLevel   === b.riskCategoryLevel &&
    a.riskCategoryName    === b.riskCategoryName &&
    a.slaDeadline         === b.slaDeadline &&
    a.contactSeverity     === b.contactSeverity &&
    aActive               === bActive &&
    aResolved             === bResolved
  )
}
